import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import _ from 'lodash';
import {
  Button,
  Card,
  DatePicker,
  DatePickerProps,
  Dropdown,
  Form,
  FormProps,
  Input,
  notification,
  Select,
  Table,
  Space
} from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { useMediaQuery } from 'react-responsive';
import { useMutation } from '@tanstack/react-query';
import { Labels } from 'services/api/type/labels.type';

import useModal from 'stores/useModal';
import useBookingStore from 'stores/useBooking';
import { useGetBooking } from 'hooks/useGetBooking';
import { useGetAllLabels } from 'hooks/useGetAllLabels';

import QUERY_KEYS from 'services/api/queryKeys';
import { RangeValueType } from 'services/api/type/common.type';
import { BOOKING_STATUS_OPTIONS } from 'constants/form';
import {
  actionCheckIn,
  actionDuplicateBookingLine,
  bookingsBatchActions,
  PAGE_SIZE_BOOKING_LIST,
  undoMovedBooking
} from 'services/api/module/booking.api';
import { getBookingListColumns, getBookingListColumnsExpand } from 'constants/table';
import 'styles/booking-list.scss';
import {
  BATCH_ACTION_TYPE,
  BookingsBatchActions,
  BookingStatus
} from 'services/api/type/booking.type';
import { queryClient } from 'index';
import { handleBookingsBatchActions } from 'components/booking-list/batchActions';
import { useGetAvailRoomType } from 'hooks/useGetAvailRoomType';
import { RoomType } from 'services/api/type/room.type';
import { FILTER_STATUS_ROOM_MAP_VALUE } from 'constants/common';
import { assignRoom } from 'services/api/module/room.api';
import { matchPath } from 'react-router-dom';
import PAGES from 'routes/constants';
import ExportBookingList from 'components/common/export-booking-list';
import { generateExportFileName, toUtcTime } from 'utils';
import { ACTION_BOOKING } from 'components/booking-list/action-booking';
import { PermissionActionTypes } from 'helper/type/action.type';

import IBButton from 'helper/permission.action';

import { useTranslation } from 'react-i18next';

const disabledDate: DatePickerProps['disabledDate'] = (current, { from }) => {
  if (from) {
    return current.isBefore(from);
  }

  return false;
};

function BookingList() {
  const { t } = useTranslation();
  const statusOptions = BOOKING_STATUS_OPTIONS(t);
  const [form] = Form.useForm();
  const status = Form.useWatch('status', form);

  const isIpadMobile = useMediaQuery({
    query: '(max-width: 991px)'
  });
  const {
    setIsOpenCancelRoom,
    setIsOpenAddService,
    setIsOpenChangeRoom,
    setIsOpenChangeDate,
    setOpenBookingDetail,
    setBookingLineId,
    setIsOpenCancelAssignRoom,
    setIsOpenAssignRoom,
    setInfoConfirmModal,
    setConfirmLoading,
    setIsOpenMoveBooking,
    setIsOpenBookingRemind
  } = useModal();
  const { setBookingLineId: setBookingLineIdBookingStore } = useBookingStore();

  const [currentAction, setCurrentAction] = useState<BATCH_ACTION_TYPE | null>(null);
  const selectedBookingLineId = useRef<number>();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const [selectedLabel, setSelectedLabel] = useState<string>('');
  const { data: listLabels } = useGetAllLabels(selectedLabel);

  const [filterOptions, setFilterOptions] = useState<any>({
    status: ''
  });

  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_BOOKING_LIST);
  const [rangeDate, setRangeDate] = useState(['', '']);
  const [roomTypeId, setRoomTypeId] = useState<number>();
  const [availableRooms, setAvailableRooms] = useState<any[]>([]);

  const { data, pagination, isFetching } = useGetBooking(filterOptions, page, pageSize);
  const { data: availRoomTypes } = useGetAvailRoomType(
    toUtcTime(rangeDate[0], 'YYYY-MM-DD HH:mm:ss'),
    toUtcTime(rangeDate[1], 'YYYY-MM-DD HH:mm:ss')
  );

  const { mutateAsync: mutateCheckIn } = useMutation({
    mutationFn: (bookingLineId: number) => actionCheckIn(bookingLineId)
  });
  const { mutateAsync: mutateAssign } = useMutation({
    mutationFn: (params: any = {}) => assignRoom(params)
  });
  const { mutateAsync: mutateDuplicate } = useMutation({
    mutationFn: (bookingLineId: number) => actionDuplicateBookingLine(bookingLineId)
  });

  const { mutateAsync: mutateBookingsBatchActions } = useMutation({
    mutationFn: (params: BookingsBatchActions) => bookingsBatchActions(params)
  });

  const { mutateAsync: mutateUndoMovedBooking } = useMutation({
    mutationFn: (bookingLineId: string) => undoMovedBooking(bookingLineId)
  });

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.KEY_ROOM, QUERY_KEYS.GET_BOOKING_LIST]
    });
    form.setFieldsValue({
      booking_id: filterOptions.booking_id,
      group_id: filterOptions.group_id
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterOptions]);

  useEffect(() => {
    setSelectedRowKeys([]);
  }, [currentAction]);

  const onFinish: FormProps['onFinish'] = async (objValue: any) => {
    if (Array.isArray(objValue['label_name'])) {
      objValue['label_name'] = objValue['label_name'].join(',');
    }
    const truthyValues: any = Object.keys(objValue)
      .filter(key => Boolean(objValue[key]))
      .reduce((cur, next) => {
        return {
          ...cur,
          [next]: objValue[next]
        };
      }, {});

    const { rangeDate, ...restFilterOptions } = truthyValues;
    if (rangeDate) {
      const checkInFrom: Dayjs = rangeDate[0];
      const checkInTo: Dayjs = rangeDate[1];
      if (checkInFrom) {
        _.set(restFilterOptions, 'check_in_from', checkInFrom.format('YYYY-MM-DD HH:mm:ss'));
      }
      if (checkInTo) {
        _.set(restFilterOptions, 'check_in_to', checkInTo.format('YYYY-MM-DD HH:mm:ss'));
      }
    }

    if (page !== 1) {
      setPage(1);
    }
    setFilterOptions(restFilterOptions);
    // Handle reset setSelectedRowKeys
    setSelectedRowKeys([]);
  };

  const onFinishFailed: FormProps['onFinishFailed'] = errorInfo => {
    console.log('Failed:', errorInfo);
  };

  const handleClickMenu = (menu: any) => {
    switch (menu.key) {
      case ACTION_BOOKING.DETAIL:
        setOpenBookingDetail(true);
        break;
      case ACTION_BOOKING.ADD_SERVICE:
        setIsOpenAddService(true);
        break;
      case ACTION_BOOKING.CANCEL_BOOKING:
        setIsOpenCancelRoom(true);
        break;
      case ACTION_BOOKING.CHANGE_ROOM:
        setIsOpenChangeRoom(true);
        break;
      case ACTION_BOOKING.CHANGE_DATE:
        setIsOpenChangeDate(true);
        break;
      case ACTION_BOOKING.GET_ROOM:
        setInfoConfirmModal(true, {
          title: t('common.confirm.checkin'),
          onOk: () => {
            setInfoConfirmModal(false);
            handleCheckIn();
          }
        });
        break;
      case ACTION_BOOKING.ASSIGN_ROOM:
        setIsOpenAssignRoom(true);
        break;
      case ACTION_BOOKING.CANCEL_ASSIGN_ROOM:
        setIsOpenCancelAssignRoom(true);
        break;
      case ACTION_BOOKING.DUPLICATE_BOOKING_LINE:
        setInfoConfirmModal(true, {
          title: t('common.confirm.duplicateBooking'),
          onOk: () => {
            setInfoConfirmModal(false);
            handleDuplicateBookingLine();
          }
        });
        break;
      case ACTION_BOOKING.MOVE_BOOKING:
        setIsOpenMoveBooking(true);
        break;
      case ACTION_BOOKING.UNDO_MOVED_BOOKING:
        setInfoConfirmModal(true, {
          title: t('common.confirm.undoMovedBooking'),
          width: 500,
          onOk: () => {
            setInfoConfirmModal(false);
            handleUndoMovedBooking();
          }
        });
        break;
      case ACTION_BOOKING.REMIND_BOOKING:
        setIsOpenBookingRemind(true);
        break;
      default:
        notification.warning({
          message: t('common.message.actionNotImplemented')
        });
        break;
    }
  };

  const handleCheckIn = async () => {
    if (selectedBookingLineId.current) {
      try {
        await mutateCheckIn(selectedBookingLineId.current);
        notification.success({
          message: t('common.message.checkIn')
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.KEY_ROOM, QUERY_KEYS.GET_BOOKING_LIST, page]
        });
        await queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_TRACKING_LOGS, selectedBookingLineId.current]
        });
        queryClient.invalidateQueries({
          queryKey: [
            QUERY_KEYS.KEY_ROOM,
            QUERY_KEYS.GET_BOOKING_DETAIL,
            selectedBookingLineId.current
          ]
        });
      } catch (err: any) {
        notification.error({
          message: err.error || t('common.error')
        });
      }
    }
  };

  const handleDuplicateBookingLine = async () => {
    if (selectedBookingLineId.current) {
      try {
        await mutateDuplicate(selectedBookingLineId.current);
        notification.success({
          message: t('common.message.duplicateBookingSuccessfully')
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.KEY_ROOM, QUERY_KEYS.GET_BOOKING_LIST, page]
        });
        queryClient.invalidateQueries({
          queryKey: [
            QUERY_KEYS.KEY_ROOM,
            QUERY_KEYS.GET_BOOKING_DETAIL,
            selectedBookingLineId.current
          ]
        });
      } catch (err: any) {
        notification.error({
          message: err.error || t('common.error')
        });
      }
    }
  };

  const handleUndoMovedBooking = async () => {
    if (selectedBookingLineId.current) {
      try {
        await mutateUndoMovedBooking(String(selectedBookingLineId.current));
        notification.success({
          message: t('common.message.undoMovedBookingSuccessfully')
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.KEY_ROOM, QUERY_KEYS.GET_BOOKING_LIST, page]
        });
        queryClient.invalidateQueries({
          queryKey: [
            QUERY_KEYS.KEY_ROOM,
            QUERY_KEYS.GET_BOOKING_DETAIL,
            selectedBookingLineId.current
          ]
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_TRACKING_LOGS]
        });
        queryClient.invalidateQueries({
          queryKey: [
            QUERY_KEYS.KEY_ROOM,
            QUERY_KEYS.GET_BOOKING_DETAIL,
            selectedBookingLineId.current
          ]
        });
      } catch (err: any) {
        notification.error({
          message: err.error || t('common.error')
        });
      }
    }
  };

  const handleChangePage = (page: number) => {
    setPage(page);
  };

  const handleRangeDateChange = (value: RangeValueType | null) => {
    let startDate: Dayjs | null = _.get(value, '0', null);
    let endDate: Dayjs | null = _.get(value, '1', null);
    if (startDate) {
      startDate = startDate.set('hour', 0).set('minute', 0).set('second', 0);
    }
    if (endDate) {
      endDate = endDate.set('hour', 23).set('minute', 59).set('second', 59);
    }
    form.setFieldValue('rangeDate', [startDate, endDate]);
  };

  useEffect(() => {
    if (roomTypeId) {
      const roomType: RoomType | undefined = _.find(availRoomTypes, {
        room_type_id: roomTypeId
      });
      if (roomType) {
        setAvailableRooms(
          roomType.rooms
            .map(item => ({
              label: item.room_name,
              value: item.room_id,
              disabled: item.room_status !== FILTER_STATUS_ROOM_MAP_VALUE.AVAILABLE
            }))
            .sort((a, b) => (a.disabled === b.disabled ? 0 : a.disabled ? 1 : -1))
        );
      }
    }
  }, [roomTypeId, availRoomTypes]);

  const handleEditRoom = async (bookingLineId: number, selectedRoomId?: number) => {
    setAvailableRooms([]);
    let bookingLineInfo = data?.find(
      booking_items =>
        booking_items.expandableRows?.find(
          bookingExpand => bookingExpand.booking_line_id === bookingLineId
        ) || booking_items.booking_line_id === bookingLineId
    );
    if (bookingLineInfo && bookingLineInfo.expandableRows) {
      const foundInExpand = bookingLineInfo.expandableRows.find(
        bookingExpand => bookingExpand.booking_line_id === bookingLineId
      );
      if (foundInExpand) {
        bookingLineInfo = foundInExpand;
      }
    }
    if (bookingLineInfo) {
      setRangeDate([bookingLineInfo.check_in, bookingLineInfo.check_out]);
      setRoomTypeId(bookingLineInfo.room_type_id);
    }
    if (selectedRoomId) {
      try {
        await mutateAssign({
          booking_line_id: bookingLineId,
          room_id: selectedRoomId
        });

        const queriesToInvalidate = [
          [QUERY_KEYS.KEY_ROOM, QUERY_KEYS.GET_AVAIL_ROOM_TYPE],
          [QUERY_KEYS.KEY_ROOM, QUERY_KEYS.GET_BOOKING_LIST]
        ];

        if (matchPath(PAGES.cleaningCalendar, location.pathname)) {
          queriesToInvalidate.push([QUERY_KEYS.GET_CLEANING_CALENDAR]);
        }

        for (const queryKey of queriesToInvalidate) {
          await queryClient.invalidateQueries({ queryKey });
        }

        notification.success({ message: t('common.confirm.changeRoomSuccessfully') });
      } catch (err: any) {
        notification.error({ message: err.error || t('common.error') });
      }
    }
  };

  const columnBookingList = useMemo(
    () =>
      getBookingListColumns(
        handleClickMenu,
        false,
        false,
        t,
        false,
        false,
        false,
        handleEditRoom,
        availableRooms,
        setRangeDate,
        setFilterOptions
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, handleClickMenu]
  );

  const columnBookingListExpand = useMemo(
    () =>
      getBookingListColumnsExpand(
        handleClickMenu,
        false,
        false,
        t,
        false,
        false,
        false,
        handleEditRoom,
        availableRooms,
        setRangeDate
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, handleClickMenu]
  );

  const onLabelChange = (value: string) => {
    setSelectedLabel(value);
  };

  // eslint-disable-next-line
  const handleSearch = useCallback(
    _.debounce(async search => {
      setSelectedLabel(search);
    }, 500),
    []
  );

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = currentAction ? { selectedRowKeys, onChange: onSelectChange } : undefined;

  const items = [
    {
      label: t('bookingPage.bookingList.actions.assignRoom'),
      key: 'assign',
      onClick: () => {
        setFilterOptions((prev: any) => ({
          ...prev,
          is_assign: true,
          status: BookingStatus.INITIAL
        }));
        setCurrentAction(BATCH_ACTION_TYPE.AUTO_ASSIGN);
      }
    },
    {
      label: t('bookingPage.bookingList.actions.unassignRoom'),
      key: 'unassign',
      onClick: () => {
        setFilterOptions((prev: any) => ({
          ...prev,
          is_assign: false,
          status: BookingStatus.INITIAL
        }));
        setCurrentAction(BATCH_ACTION_TYPE.UNASSIGN);
      }
    },
    {
      label: t('bookingPage.bookingList.actions.checkInRoom'),
      key: 'check_in',
      onClick: () => {
        const startOfDate = dayjs().startOf('day');
        const endOfDate = dayjs().endOf('day');
        handleRangeDateChange([startOfDate, endOfDate]);
        setFilterOptions({
          check_in_from: startOfDate.utc().format('YYYY-MM-DD HH:mm:ss'),
          check_in_to: endOfDate.utc().format('YYYY-MM-DD HH:mm:ss'),
          is_assign: false,
          status: BookingStatus.INITIAL
        });
        setCurrentAction(BATCH_ACTION_TYPE.CHECK_IN);
      }
    },
    {
      label: t('bookingPage.bookingList.actions.checkOutRoom'),
      key: 'check_out',
      onClick: () => {
        setFilterOptions((prev: any) => ({ ...prev, status: BookingStatus.CHECK_IN }));
        setCurrentAction(BATCH_ACTION_TYPE.CHECK_OUT);
      }
    }
  ];

  const generateConfirm = (
    actionType: BATCH_ACTION_TYPE,
    confirmTitle: string,
    successMessage: string
  ) => {
    setInfoConfirmModal(true, {
      title: confirmTitle,
      onOk: async () => {
        setInfoConfirmModal(false);
        setConfirmLoading(true);
        try {
          const numericSelectedRowKeys = selectedRowKeys
            .map(key => Number(key))
            .filter(key => !isNaN(key));

          await mutateBookingsBatchActions({
            action: actionType,
            booking_ids: numericSelectedRowKeys
          });

          await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.KEY_ROOM] });

          notification.success({ message: successMessage });
          setSelectedRowKeys([]);
        } catch (err: any) {
          notification.error({ message: err.error || t('common.error') });
        } finally {
          setConfirmLoading(false);
        }
      }
    });
  };

  const handleAction = async () => {
    if (currentAction) {
      await handleBookingsBatchActions(currentAction, generateConfirm, t);
    }
  };

  const handleCancelBookingsBatchActions = () => {
    setCurrentAction(null);
    setSelectedRowKeys([]);
    setFilterOptions({ status: '' });
    handleRangeDateChange(null);
  };

  const message = useMemo(() => {
    switch (currentAction) {
      case BATCH_ACTION_TYPE.AUTO_ASSIGN:
        return {
          key: 'assignRoom',
          message: t('common.actions.assignRoom'),
          permission: PermissionActionTypes.ASSIGN_ROOM
        };

      case BATCH_ACTION_TYPE.UNASSIGN:
        return {
          key: 'unassignRoom',
          message: t('common.actions.unAssignRoom'),
          permission: PermissionActionTypes.UNASSIGN_ROOM
        };

      case BATCH_ACTION_TYPE.CHECK_IN:
        return {
          key: 'checkIn',
          message: t('common.actions.checkIn'),
          permission: PermissionActionTypes.CHECK_IN
        };

      case BATCH_ACTION_TYPE.CHECK_OUT:
        return {
          key: 'checkOut',
          message: t('common.actions.checkOut'),
          permission: PermissionActionTypes.CHECK_OUT
        };

      default:
        return { message: t('common.actions.confirm'), permission: '' };
    }
  }, [currentAction, t]);

  return (
    <div className="pms-booking-list">
      <div style={{ marginBottom: 8 }}>
        <Dropdown menu={{ items }} trigger={['click']}>
          <Button type="primary">
            {t('bookingPage.bookingList.chooseAction')}
            <DownOutlined />
          </Button>
        </Dropdown>{' '}
      </div>
      {currentAction && (
        <IBButton
          permission={message.permission}
          type="primary"
          onClick={handleAction}
          disabled={selectedRowKeys.length == 0}
          style={{ display: currentAction ? 'inline-block' : 'none' }}
        >
          {t(`bookingPage.bookingList.confirm.${message.key}`)}
        </IBButton>
      )}{' '}
      {currentAction && (
        <Button
          type="primary"
          onClick={handleCancelBookingsBatchActions}
          style={{ display: currentAction ? 'inline-block' : 'none' }}
        >
          {t('common.actions.cancel')}
        </Button>
      )}
      <Card title={t('bookingPage.bookingList.title')} style={{ width: '100%' }}>
        <Form
          form={form}
          name="booking-filter-form"
          layout={isIpadMobile ? 'vertical' : 'inline'}
          initialValues={{
            booking_id: '',
            rangeDate: null,
            status: '',
            label_name: null,
            search: ''
          }}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
        >
          <div className="pms-booking-list__filter">
            <Form.Item name="search" style={{ flex: 1 }}>
              <Input placeholder={t('bookingPage.bookingList.searchPlaceholder')} />
            </Form.Item>

            <Form.Item className="labels-search" name="label_name">
              <Select
                value={selectedLabel}
                options={listLabels.map((label: Labels) => ({
                  value: label.name,
                  label: label.name,
                  color: label.color
                }))}
                placeholder={t('bookingPage.bookingList.groupPlaceholder')}
                mode="multiple"
                onChange={onLabelChange}
                onSearch={handleSearch}
                onClear={() => setSelectedLabel('')}
                showSearch
                allowClear
                optionRender={option => {
                  return option.label ? (
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '5px 10px'
                      }}
                    >
                      <span
                        style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: option.data.color || '#f0f0f0',
                          marginRight: '8px'
                        }}
                      />
                      {option.label}
                    </span>
                  ) : null;
                }}
              />
            </Form.Item>

            <Form.Item className="group-id" name="group_id">
              <Input placeholder={t('bookingPage.bookingList.groupIdPlaceholder')} />
            </Form.Item>

            <Form.Item name="booking_id">
              <Input placeholder={t('bookingPage.bookingList.bookingIdPlaceholder')} />
            </Form.Item>

            <Form.Item name="rangeDate">
              <DatePicker.RangePicker
                className="w-full"
                placeholder={[t('common.time.startDate'), t('common.time.endDate')]}
                disabledDate={disabledDate}
                format="YYYY-MM-DD HH:mm"
                onChange={handleRangeDateChange}
              />
            </Form.Item>

            <Form.Item name="status">
              <Select
                options={statusOptions}
                labelRender={option => (
                  <span>
                    {t('common.bookingData.status')}: {option.label}
                  </span>
                )}
              />
            </Form.Item>

            <Space>
              <Button htmlType="submit" className="ant-btn-secondary btn-submit">
                {t('common.actions.submitButton')}
              </Button>
              <ExportBookingList
                fileName={generateExportFileName('booking-list')}
                selectedState={status}
                filterOptions={filterOptions}
                disabled={!filterOptions.check_in_from || !filterOptions.check_in_to}
              />
            </Space>
          </div>
        </Form>

        <div className="pms-booking-list__table">
          <Table
            loading={isFetching}
            rowKey="booking_id"
            columns={columnBookingList}
            dataSource={data}
            onRow={record => {
              return {
                onClick: () => {
                  setBookingLineId(record.booking_line_id);
                  setBookingLineIdBookingStore(record.booking_line_id);
                  selectedBookingLineId.current = record.booking_line_id;
                  console.log('record.booking_line_id', record.booking_line_id);
                } // click row
              };
            }}
            pagination={{
              current: page,
              position: ['bottomCenter'],
              total: pagination.total,
              pageSize: pageSize,
              locale: { items_per_page: `/ ${t('common.page')}` },
              showLessItems: true,
              showTotal: total => `${total} ${t('bookingPage.bookingList.item')}`,
              onChange: (page: number) => handleChangePage(page),
              onShowSizeChange: (current, size) => {
                setPageSize(size);
              }
            }}
            locale={{
              emptyText: <span className="empty-data">{t('common.actions.noData')}</span>
            }}
            rowSelection={rowSelection}
            scroll={{ x: 500, y: 'calc(96vh - 400px)' }}
            expandable={{
              expandedRowRender: record => {
                return (
                  <Table
                    rowKey="booking_line_id"
                    columns={columnBookingListExpand}
                    showHeader={false}
                    dataSource={record.expandableRows}
                    pagination={false}
                    scroll={{ x: 500 }}
                    onRow={record => {
                      return {
                        onClick: () => {
                          setBookingLineId(record.booking_line_id);
                          setBookingLineIdBookingStore(record.booking_line_id);
                          selectedBookingLineId.current = record.booking_line_id;
                          console.log('record.booking_line_id', record.booking_line_id);
                        } // click row
                      };
                    }}
                  />
                );
              },
              rowExpandable: record => !_.isEmpty(record.expandableRows),
              columnWidth: 30,
              defaultExpandAllRows: true
            }}
          />
        </div>
      </Card>
    </div>
  );
}

export default BookingList;
